import axios from 'axios'
import { getBearerToken } from '@/utils/authToken'
import { documentFileName } from '@/utils/documentUrl'


export async function downloadFile(fileOrUrl: File | string | undefined) {
  if (!fileOrUrl) {
    return;
  }

  if (typeof fileOrUrl === 'string') {
    // Handle URL case
    try {
      const response = await fetch(fileOrUrl, { method: 'GET' });

      if (!response.ok) {
        console.error('Error downloading file:', response);
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const fileName = documentFileName(fileOrUrl, 'download.pdf');

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // Fallback to direct link approach
      if (!fileOrUrl || typeof fileOrUrl !== 'string') {
        console.error('Invalid file URL provided:', fileOrUrl);
        return;
      }

      try {
        const link = document.createElement('a');
        link.href = fileOrUrl;
        link.download = documentFileName(fileOrUrl, 'download.pdf');
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (innerError) {
        console.error('Failed to download file:', innerError);
      }
    }
  } else {
    // Handle File object case
    const blob = new Blob([fileOrUrl], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = fileOrUrl.name;

    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}


export async function updateFilesMetadata(filesMetadata: { key: string, filename: string }[]) {
  const accessToken = await getBearerToken()

  try {
    await axios.put(
      `${import.meta.env.VITE_API_URL}/api/accountability/cloud-file-service/update-metadata/`,
      filesMetadata.map(({ key, filename }) => ({
        file_key: key,
        content_type: getContentTypeFromExtension(filename),
      })),
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )
  } catch (error) {
    console.error("Failed to update metadata for files:", filesMetadata, error)
  }
}


export function getContentTypeFromExtension(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase() || '';

  const mimeTypes: { [key: string]: string } = {
    'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'png': 'image/png', 'gif': 'image/gif',
    'bmp': 'image/bmp', 'tiff': 'image/tiff', 'webp': 'image/webp', 'svg': 'image/svg+xml', 'ico': 'image/x-icon',
    'heic': 'image/heic', 'heif': 'image/heif',
    'pdf': 'application/pdf', 'doc': 'application/msword', 'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel', 'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint', 'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain', 'csv': 'text/csv', 'json': 'application/json', 'xml': 'application/xml', 'html': 'text/html', 'md': 'text/markdown',
    'mp4': 'video/mp4', 'mov': 'video/quicktime', 'avi': 'video/x-msvideo', 'wmv': 'video/x-ms-wmv',
    'flv': 'video/x-flv', 'webm': 'video/webm', 'mkv': 'video/x-matroska',
    'mp3': 'audio/mpeg', 'wav': 'audio/wav', 'ogg': 'audio/ogg', 'm4a': 'audio/mp4', 'flac': 'audio/flac',
    'zip': 'application/zip', 'rar': 'application/vnd.rar', '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar', 'gz': 'application/gzip',
    'exe': 'application/vnd.microsoft.portable-executable', 'dmg': 'application/x-apple-diskimage',
    'iso': 'application/x-iso9660-image'
  };

  return mimeTypes[extension] || 'application/octet-stream'; // Fallback si no se encuentra
}
